import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizationService, PermissionService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { ZendraLocalizationPipe } from '@zendra/shared';
import {
    GisProtectedAreaInconsistencyPlanningOrchestratorService,
    GisStandAreaDifferencePlanningOrchestratorService,
    GisStandInconsistencyCase,
    GisStandInconsistencyPlanningOrchestratorService,
    OtherUseInGisInconsistencyPlanningOrchestratorService,
    GisRegisterInconsistencyPlanningOrchestratorService,
    GisRegisterInconsistencyCase,
} from 'orchestrator';
import { catchError, firstValueFrom, forkJoin, map, Observable, of } from 'rxjs';
import { GisInconsistencyGroupCard } from '../gis-inconsistency-group-card/gis-inconsistency-group-card';
import { GISINCONSISTENCYDASHBOARD_CONFIG, GROUP_SEEDS } from './gis-inconsistency-dashboard.config';
import { STAND_AREA_DIFFERENCE_THRESHOLD } from '../gis-stand-area-difference/gis-stand-area-difference.config';
import { GISSTANDINCONSISTENCY_PERMISSION } from '../gis-stand-inconsistency/gis-stand-inconsistency.config';
import { GISREGISTERINCONSISTENCY_PERMISSION } from '../gis-register-inconsistency-only-in-gis/gis-register-inconsistency-only-in-gis.config';
import { GISPROTECTEDAREAINCONSISTENCY_PERMISSION } from '../gis-protected-area-inconsistency-only-in-gis/gis-protected-area-inconsistency-only-in-gis.config';
import {
    GisInconsistencyGroup,
    GisInconsistencyItem,
    ItemSeed,
} from './gis-inconsistency-dashboard.models';
import { OtherUseInZendraInconsistencyPlanningOrchestratorService } from 'orchestrator/proxy';

type CountResult = Pick<GisInconsistencyItem, 'count' | 'countState'>;

@Component({
    selector: 'gis-inconsistency-dashboard',
    imports: [
        ZendraLocalizationPipe,
        GisInconsistencyGroupCard,
    ],
    templateUrl: './gis-inconsistency-dashboard.html',
    styleUrl: './gis-inconsistency-dashboard.scss',
})
export class GisInconsistencyDashboard implements OnInit {
    private readonly localizationService = inject(LocalizationService);
    private readonly permissionService = inject(PermissionService);
    private readonly orchestratorService = inject(GisStandInconsistencyPlanningOrchestratorService);
    private readonly registerInconsistencyService = inject(GisRegisterInconsistencyPlanningOrchestratorService);
    private readonly gisStandAreaDifferenceService = inject(GisStandAreaDifferencePlanningOrchestratorService);
    private readonly otherUsesOnlyInZendraService = inject(OtherUseInZendraInconsistencyPlanningOrchestratorService);
    private readonly otherUsesOnlyInGisService = inject(OtherUseInGisInconsistencyPlanningOrchestratorService);
    private readonly router = inject(Router);
    private readonly toaster = inject(ToasterService);
    private readonly canViewStands = this.permissionService.getGrantedPolicy(GISSTANDINCONSISTENCY_PERMISSION);
    private readonly canViewRegisters = this.permissionService.getGrantedPolicy(GISREGISTERINCONSISTENCY_PERMISSION);
    private readonly protectedAreaSnapshotService = inject(GisProtectedAreaInconsistencyPlanningOrchestratorService);
    private readonly canViewProtectedAreas = this.permissionService.getGrantedPolicy(GISPROTECTEDAREAINCONSISTENCY_PERMISSION);
    private readonly canViewOtherUses = this.permissionService.getGrantedPolicy('PlanningLocation.OtherUses');

    readonly config = GISINCONSISTENCYDASHBOARD_CONFIG;
    readonly groups = signal<GisInconsistencyGroup[]>(this.createGroups());

    ngOnInit(): void {
        void this.loadAvailableCounts();
    }

    async openQuery(item: GisInconsistencyItem): Promise<void> {
        if (!item.routePath) {
            return;
        }

        await this.router.navigate(['/geographic', item.routePath]);
    }

    private async loadAvailableCounts(): Promise<void> {
        try {
            const counts = await firstValueFrom(
                forkJoin({
                    standsOnlyInGis: this.getStandInconsistencyCount(GisStandInconsistencyCase.OnlyInGis),
                    standsOnlyInZendra: this.getStandInconsistencyCount(GisStandInconsistencyCase.OnlyInZendra),
                    standsDisabledInZendra: this.getStandInconsistencyCount(
                        GisStandInconsistencyCase.DisabledInZendra,
                    ),
                    standsAreaDifference: this.getStandAreaDifferenceCount(),
                    propertiesOnlyInGis: this.getRegisterInconsistencyCount(GisRegisterInconsistencyCase.OnlyInGis),
                    propertiesOnlyInZendra: this.getRegisterInconsistencyCount(GisRegisterInconsistencyCase.OnlyInZendra),
                    protectedAreasOnlyInGis: this.getProtectedAreaInconsistencyCount(),
                    protectedAreasOnlyInZendra: this.getProtectedAreaOnlyInZendraCount(),
                    otherUsesOnlyInGis: this.getOtherUsesOnlyInGisCount(),
                    otherUsesOnlyInZendra: this.canViewOtherUses ? this.loadCount(this.otherUsesOnlyInZendraService.getList({ skipCount: 0, maxResultCount: 1 }, { skipHandleError: true })) : of(null),
                }),
            );

            this.groups.update(groups =>
                groups.map(group => ({
                    ...group,
                    items: group.items.map(item => {
                        const result = counts[item.key as keyof typeof counts];
                        return result ? { ...item, ...result } : item;
                    }),
                })),
            );

            if (Object.values(counts).some(result => result?.countState === 'error')) {
                this.showCountsError();
            }
        } catch {
            this.showCountsError();
        }
    }

    private showCountsError(): void {
        this.toaster.error(
            this.localizationService.instant('Geographic::Entity.GisInconsistencyDashboard.LoadCountsError'),
        );
    }

    private loadCount(request: Observable<{ totalCount?: number }>): Observable<CountResult> {
        return request.pipe(
            map(response => ({ count: response.totalCount ?? 0, countState: 'loaded' } as const)),
            catchError(() => of({ count: null, countState: 'error' } as const)),
        );
    }

    private getStandInconsistencyCount(caseFilter: GisStandInconsistencyCase) {
        if (!this.canViewStands) {
            return of(null);
        }

        return this.loadCount(this.orchestratorService
            .getList({
                case: caseFilter,
                skipCount: 0,
                maxResultCount: 1,
            }, { skipHandleError: true }));
    }

    private getStandAreaDifferenceCount() {
        if (!this.canViewStands) {
            return of(null);
        }

        return this.loadCount(this.gisStandAreaDifferenceService
            .getList({
                isActive: true,
                areaDifferenceGreaterThan: STAND_AREA_DIFFERENCE_THRESHOLD,
                skipCount: 0,
                maxResultCount: 1,
            }, { skipHandleError: true }));
    }

    private getRegisterInconsistencyCount(caseFilter: GisRegisterInconsistencyCase) {
        if (!this.canViewRegisters) {
            return of(null);
        }

        return this.loadCount(this.registerInconsistencyService
            .getList({ case: caseFilter, skipCount: 0, maxResultCount: 1 }, { skipHandleError: true }));
    }

    private getOtherUsesOnlyInGisCount() {
        return this.canViewOtherUses ? this.loadCount(this.otherUsesOnlyInGisService.getList({ skipCount: 0, maxResultCount: 1 }, { skipHandleError: true })) : of(null);
    }

    private createItem([key, label, options]: ItemSeed): GisInconsistencyItem {
        let canView = true;

        if (key.startsWith('stands')) {
            canView = this.canViewStands;
        } else if (key === 'propertiesOnlyInGis' || key === 'propertiesOnlyInZendra') {
            canView = this.canViewRegisters;
        } else if (key === 'protectedAreasOnlyInGis' || key === 'protectedAreasOnlyInZendra') {
            canView = this.canViewProtectedAreas;
        } else if (key.startsWith('otherUses')) {
            canView = this.canViewOtherUses;
        }

        return {
            key,
            label,
            count: null,
            ...(canView && options?.routePath ? { countState: 'loading' as const } : {}),
            ...(canView ? options : { ...options, routePath: undefined }),
        };
    }

    private createGroups(): GisInconsistencyGroup[] {
        return GROUP_SEEDS.map(group => ({
            ...group,
            items: group.items.map(item => this.createItem(item)),
        }));
    }

    private getProtectedAreaInconsistencyCount() {
        if (!this.canViewProtectedAreas) {
            return of(null);
        }
        return this.loadCount(this.protectedAreaSnapshotService.getOnlyInGis(
            { skipCount: 0, maxResultCount: 1 }, { skipHandleError: true },
        ));
    }

    private getProtectedAreaOnlyInZendraCount() {
        if (!this.canViewProtectedAreas) return of(null);
        return this.loadCount(this.protectedAreaSnapshotService.getOnlyInZendra(
            { skipCount: 0, maxResultCount: 1 }, { skipHandleError: true },
        ));
    }
}
